import propTypes from "prop-types"
import React, { useMemo, useState } from "react"
import { Alert, Button, Col, Table } from "reactstrap"

import { Loading } from "components"
import { formatDateParam } from "utils/date-filters"
import ListHeader from "ui-componets/list-header"
import ListFilterBar from "ui-componets/list-filter-bar"
import TablePaginationFooter from "components/TablePaginationFooter"

const movementLabels = {
  add: "Ingreso",
  withdraw: "Retiro",
  transfer: "Movimiento",
  adjustment: "Ajuste",
}

const accountLabels = {
  cash_register: "Caja registradora",
  cash_account: "Dinero en Resguardo",
}

const actionButtonStyle = {
  width: "30px",
  height: "30px",
  padding: 0,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
}

const actionIconStyle = { fontSize: "0.85rem" }

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" })

const formatDateTime = (value) => {
  if (!value) {
    return "—"
  }

  return new Intl.DateTimeFormat("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(new Date(value))
}

const formatDateOnly = (value) => {
  if (!value) return "—"
  // If value is a plain date (YYYY-MM-DD) treat it as local midnight to avoid UTC shift
  const isPlainDate = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
  const dt = isPlainDate ? new Date(`${value}T00:00:00`) : new Date(value)
  return new Intl.DateTimeFormat("es-MX", { day: "2-digit", month: "short", year: "numeric" }).format(dt)
}

const formatOperationDateTime = (cashOperationDay, fallbackCreatedAt) => {
  if (!cashOperationDay) {
    return fallbackCreatedAt ? formatDateTime(fallbackCreatedAt) : "—"
  }

  return formatDateOnly(cashOperationDay.operation_date)
}

const parseToDate = (value) => {
  if (!value) return null
  const s = String(value)
  const isPlainDate = /^\d{4}-\d{2}-\d{2}$/.test(s)
  return isPlainDate ? new Date(`${s}T00:00:00`) : new Date(s)
}

const getMovementDateKey = (movement) => {
  const dateSource = movement?.cash_operation_day?.operation_date || movement?.created_at
  if (!dateSource) return ""

  const dt = parseToDate(dateSource)
  return dt ? formatDateParam(dt) : ""
}

const getMovementLabel = (movementType) => movementLabels[movementType] || movementType || "—"

// movement text now uses fixed styling (black + bold)

const getAccountLabel = (accountType) => accountLabels[accountType] || accountType || "—"

const normalizeFilterValues = (value) => {
  if (Array.isArray(value)) {
    return value
  }

  if (!value || value === "all") {
    return []
  }

  return [value]
}

const canDeleteMovement = (movement, currentCashOperationDay) => {
  if (movement?.movement_type !== "withdraw") return false

  const movementCashOperationDayId = movement?.cash_operation_day?.id
  const currentCashOperationDayId = currentCashOperationDay?.id

  if (!movementCashOperationDayId || !currentCashOperationDayId) return false

  return String(movementCashOperationDayId) === String(currentCashOperationDayId)
}

const CashMovementsHistoryTable = ({
  movements,
  isLoading,
  isError,
  error,
  title,
  description,
  headerActions,
  headerRightActions,
  showFilters,
  showMovementTypeColumn,
  showAccountColumn,
  showActionsColumn,
  showDateColumn,
  defaultTypeFilter,
  defaultDateFilter,
  currentCashOperationDay,
  currentPage = 1,
  totalCount = 0,
  pageSize = 30,
  onChangePage = () => {},
  onFiltersChange = () => {},
  onDeleteMovement = () => {},
}) => {
  const [dateFilter, setDateFilter] = useState(defaultDateFilter)
  const [typeFilter, setTypeFilter] = useState(defaultTypeFilter)
  const [searchTxt, setSearchTxt] = useState("")
  const reasonColumnWidth = showDateColumn ? "35%" : "70%"

  const emitFiltersChange = (nextFilters) => {
    onFiltersChange({
      typeFilter,
      searchTxt,
      dateFilter,
      ...nextFilters,
    })
  }

  const selects = [
    {
      name: "type",
      label: "Tipo",
      value: typeFilter,
      onChange: (v) => {
        setTypeFilter(v)
        emitFiltersChange({ typeFilter: v })
      },
      options: [
        { value: "all", label: "Todos" },
        { value: "add", label: "Ingreso" },
        { value: "withdraw", label: "Retiro" },
        { value: "transfer", label: "Movimiento" },
        { value: "adjustment", label: "Ajuste" },
      ],
    },
  ]

  const filteredMovements = useMemo(() => {
    if (!movements) return []

    const selectedTypes = normalizeFilterValues(typeFilter)

    return movements.filter((m) => {
      // filter by type
      if (selectedTypes.length && !selectedTypes.includes(m.movement_type)) return false

      // filter by reason (search)
      if (searchTxt && searchTxt.trim()) {
        const needle = searchTxt.trim().toLowerCase()
        const reason = (m.reason || "").toLowerCase()
        if (!reason.includes(needle)) return false
      }

      // filter by date range if provided (compare YYYY-MM-DD to avoid timezone issues)
      if (dateFilter && (dateFilter.startDate || dateFilter.endDate)) {
        const movementDateKey = getMovementDateKey(m)
        if (!movementDateKey) return false

        if (dateFilter.startDate && movementDateKey < dateFilter.startDate) return false
        if (dateFilter.endDate && movementDateKey > dateFilter.endDate) return false
      }

      return true
    })
  }, [movements, typeFilter, searchTxt, dateFilter])
  // With server-side pagination the parent provides `movements` for the current page and `totalCount`.
  const paginatedMovements = filteredMovements
  return (
    <Col xs="12" className="mt-2">
      <div className="p-4 rounded">
        <div className="d-flex align-items-center justify-content-between gap-3 mb-3 flex-wrap pt-5">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            {headerActions && <div className="d-flex flex-wrap gap-2 align-items-center">{headerActions}</div>}
            <div>
              <h4 className="mb-1">{title}</h4>
              <p className="text-muted mb-0">{description}</p>
            </div>
          </div>
          {headerRightActions && (
            <div className="d-flex flex-wrap gap-2 align-items-center">{headerRightActions}</div>
          )}
        </div>

        {isError && (
          <Alert color="danger" className="mb-3">
            {error?.errors?.map((item) => item?.message).filter(Boolean).join(" ")}
          </Alert>
        )}

        {showFilters && (
          <ListHeader
            handleOnChangeSearch={(e) => {
              const nextSearchTxt = e.target.value
              setSearchTxt(nextSearchTxt)
              emitFiltersChange({ searchTxt: nextSearchTxt })
            }}
            searchTxt={searchTxt}
            filtersContent={
              <ListFilterBar
                dateFilter={dateFilter}
                onDateFilterChange={(next) => {
                  setDateFilter(next)
                  emitFiltersChange({ dateFilter: next })
                }}
                selects={selects}
                onClear={() => {
                  const nextDateFilter = { preset: "", startDate: "", endDate: "" }
                  setDateFilter(nextDateFilter)
                  setTypeFilter(defaultTypeFilter)
                  setSearchTxt("")
                  onFiltersChange({
                    typeFilter: defaultTypeFilter,
                    searchTxt: "",
                    dateFilter: nextDateFilter,
                  })
                }}
                autoApply={true}
              />
            }
          />
        )}

        <div className="mt-3">
          {isLoading ? (
            <div className="text-center py-4">
              <Loading isLoading size={2} />
            </div>
          ) : filteredMovements.length ? (
            <div className="table-responsive">
              <Table
                hover
                size="sm"
                style={{ tableLayout: "fixed" }}
                className="align-middle mb-0 table-hover-effect table-interactive"
              >
                <thead>
                  <tr>
                    {showDateColumn && <th>Fecha</th>}
                    {showMovementTypeColumn && <th style={{ width: "110px" }}>Tipo</th>}
                    {showAccountColumn && <th style={{ width: "160px" }}>Origen / Destino</th>}
                    <th className="text-end" style={{ width: "120px" }}>Monto</th>
                    <th style={{ width: reasonColumnWidth }}>Motivo</th>
                    {showActionsColumn && <th className="text-center">Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {paginatedMovements.map((movement) => (
                    <tr key={movement.id}>
                      {showDateColumn && (
                        <td>
                          {formatOperationDateTime(movement?.cash_operation_day, movement.created_at)}
                        </td>
                      )}
                      {showMovementTypeColumn && (
                        <td style={{ width: "110px" }}>
                          <span className="fw-semibold text-dark">{getMovementLabel(movement.movement_type)}</span>
                        </td>
                      )}
                      {showAccountColumn && (
                        <td style={{ width: "160px" }}>
                          {movement.movement_type === "add"
                            ? `Hacia ${getAccountLabel(movement.destination_account_type)}`
                            : movement.movement_type === "withdraw"
                            ? `Desde ${getAccountLabel(movement.source_account_type)}`
                            : movement.movement_type === "adjustment"
                            ? getAccountLabel(movement.destination_account_type || movement.source_account_type)
                            : `${getAccountLabel(movement.source_account_type)} → ${getAccountLabel(
                                movement.destination_account_type
                              )}`}
                        </td>
                      )}
                      <td className="text-end fw-semibold" style={{ width: "120px" }}>
                        {formatCurrency(movement.cash)}
                      </td>
                      <td
                        style={{
                          width: reasonColumnWidth,
                          maxWidth: "none",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {movement.reason || "—"}
                      </td>
                      {showActionsColumn && (
                        <td className="text-center">
                          {canDeleteMovement(movement, currentCashOperationDay) ? (
                            <Button
                              color="danger"
                              size="sm"
                              style={actionButtonStyle}
                              title="Eliminar retiro"
                              onClick={() => onDeleteMovement(movement)}
                            >
                              <i className="fa fa-trash" style={actionIconStyle} />
                            </Button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            <TablePaginationFooter
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              itemLabel="movimientos"
              isLoading={isLoading}
              isFetching={false}
              onChangePage={onChangePage}
            />
            </div>
          ) : (
          <div className="text-center text-muted py-4">Todavía no hay movimientos registrados.</div>
          )}
        </div>
      </div>
    </Col>
  )
}

CashMovementsHistoryTable.propTypes = {
  movements: propTypes.array,
  isLoading: propTypes.bool,
  isError: propTypes.bool,
  error: propTypes.any,
  title: propTypes.string,
  description: propTypes.string,
  headerActions: propTypes.node,
  headerRightActions: propTypes.node,
  showFilters: propTypes.bool,
  showDateColumn: propTypes.bool,
  showMovementTypeColumn: propTypes.bool,
  showAccountColumn: propTypes.bool,
  showActionsColumn: propTypes.bool,
  defaultTypeFilter: propTypes.string,
  defaultDateFilter: propTypes.shape({
    preset: propTypes.string,
    startDate: propTypes.string,
    endDate: propTypes.string,
  }),
  currentCashOperationDay: propTypes.object,
  currentPage: propTypes.number,
  totalCount: propTypes.number,
  pageSize: propTypes.number,
  onChangePage: propTypes.func,
  onFiltersChange: propTypes.func,
  onDeleteMovement: propTypes.func,
}

CashMovementsHistoryTable.defaultProps = {
  title: "Retiros del día",
  description: "Retiros desde Dinero en Resguardo.",
  headerActions: null,
  headerRightActions: null,
  showFilters: true,
  showDateColumn: true,
  showMovementTypeColumn: true,
  showAccountColumn: true,
  showActionsColumn: false,
  defaultTypeFilter: "withdraw",
  defaultDateFilter: { preset: "", startDate: "", endDate: "" },
  currentCashOperationDay: null,
  currentPage: 1,
  totalCount: 0,
  pageSize: 30,
  onChangePage: () => {},
  onFiltersChange: () => {},
  onDeleteMovement: () => {},
}

export default CashMovementsHistoryTable
